import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ActivityLog } from '@/components/ActivityLog';
import { toast } from 'sonner';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import ManageAddresses from '@/components/common/ManageAddresses';
import { useCounties } from '@/hooks/useCounties';
import { US_STATES, stateNameToCode } from '@/constants/usStates';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  ArrowLeft, User, Phone, Mail, MapPin, Building, Shield,
  FileText, Receipt, Wrench, FolderKanban, Image, ClipboardList,
  Pencil, Plus, ExternalLink, Calendar, DollarSign, Eye,
  Upload, File, Video, Trash2, X, ChevronDown, UserPlus, MessagesSquare
} from 'lucide-react';
import ClickToCall from '@/components/voice/ClickToCall';
import CommunicationsTimeline from '@/components/voice/CommunicationsTimeline';
import { useDialClick } from '@/hooks/usePhoneDial';

// Format phone number as (XXX) XXX-XXXX
const formatPhoneNumber = (value) => {
  const digits = value.replace(/\D/g, '');
  const limited = digits.slice(0, 10);
  if (limited.length === 0) return '';
  if (limited.length <= 3) return `(${limited}`;
  if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
};

// Fix S (2026-09-14): additional_phones/emails pueden venir como string
// suelto (legacy, nunca se migra) o como {name, phone, label} (nuevo).
const phoneEntryOf = (p) => (typeof p === 'string' ? { name: '', phone: p, label: '' } : { name: p?.name || '', phone: p?.phone || '', label: p?.label || '' });
const emailEntryOf = (e) => (typeof e === 'string' ? { name: '', email: e } : { name: e?.name || '', email: e?.email || '' });

// Fix S (2026-09-14): direccion completa de 3 lineas (calle / ciudad, estado zip / condado).
const stateFullName = (code) => {
  if (!code) return '';
  const s = US_STATES.find(s => s.code === String(code).toUpperCase());
  return s ? s.name : code;
};

const COUNTIES = null; // Replaced by useCounties hook
const ACCOUNT_TYPES = ['residential', 'commercial', 'subcontractor', 'vendor', 'other'];
const ACCOUNT_TYPE_CONFIG = {
  residential: { label: 'Residential', type: 'customer', is_customer: true },
  commercial: { label: 'Commercial', type: 'customer', is_customer: true },
  subcontractor: { label: 'Subcontractor', type: 'subcontractor', is_customer: false },
  vendor: { label: 'Vendor', type: 'vendor', is_customer: false },
  other: { label: 'Other', type: 'customer', is_customer: true },
};

export default function ContactDetailPage() {
  const dialClick = useDialClick();
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const counties = useCounties();
  
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'info');
  
  // Related data
  const [estimates, setEstimates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [jobs, setJobs] = useState([]);

  const [permits, setPermits] = useState([]);
  const [files, setFiles] = useState([]);
  // Fix (2026-09-14 cont.): calculado en vivo por el backend en GET/PUT contacts, no un campo guardado.
  const relatedProjects = contact?.related_projects || [];
  
  // Upload dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [form, setForm] = useState({});

  // Manage Addresses dialog
  const [manageAddrOpen, setManageAddrOpen] = useState(false);

  const TYPE_LABELS = { 
    customer: t('contacts.filters.customers'), 
    vendor: t('contacts.filters.vendors'), 
    subcontractor: t('contacts.subcontractor'),
    residential: 'Residential',
    commercial: 'Commercial',
    other: 'Other'
  };

  const fetchContact = useCallback(async () => {
    try {
      const res = await api.get(`/contacts/${id}`);
      setContact(res.data);
      // Derive account_type from existing data for backward compatibility
      let accountType = res.data.account_type || 'residential';
      if (!ACCOUNT_TYPE_CONFIG[accountType]) {
        if (res.data.type === 'vendor') accountType = 'vendor';
        else if (res.data.type === 'subcontractor') accountType = 'subcontractor';
        else if (res.data.company_name) accountType = 'commercial';
        else accountType = 'residential';
      }
      const cfg = ACCOUNT_TYPE_CONFIG[accountType];
      setForm({
        first_name: res.data.first_name || '',
        last_name: res.data.last_name || '',
        company_name: res.data.company_name || '',
        email: res.data.email || '',
        phone: res.data.phone || '',
        additional_phones: res.data.additional_phones || [],
        additional_emails: res.data.additional_emails || [],
        contact_people: res.data.contact_people || [],
        type: cfg.type,
        account_type: accountType,
        is_customer: cfg.is_customer,
        address: res.data.address || '',
        city: res.data.city || '',
        county: res.data.county || '',
        state: stateNameToCode(res.data.state) || 'FL',
        zip: res.data.zip || '',
        has_drilling_license: res.data.has_drilling_license || false,
        license_number: res.data.license_number || '',
        notes: res.data.notes || '',
        subcontractor_category: res.data.subcontractor_category || '',
        payment_type: res.data.payment_type || '',
        commission_type: res.data.commission_type || ''
      });
    } catch {
      toast.error(t('errors.loadingError'));
      navigate('/contacts');
    }
  }, [id, navigate, t]);

  const fetchRelatedData = useCallback(async () => {
    try {
      const [estRes, invRes, jobsRes, permRes, filesRes] = await Promise.all([
        api.get('/estimates', { params: { page_size: 500 } }).catch(() => ({ data: { items: [] } })),
        api.get('/invoices', { params: { page_size: 500 } }).catch(() => ({ data: { items: [] } })),
        api.get('/jobs', { params: { page_size: 500 } }).catch(() => ({ data: { items: [] } })),
        api.get(`/permits/by-contact/${id}`).catch(() => ({ data: [] })),
        api.get(`/files/by-contact/${id}`).catch(() => ({ data: [] }))
      ]);
      
      // Filter by contact_id or customer_id
      setEstimates((estRes.data.items || estRes.data).filter(e => e.contact_id === id || e.customer_id === id));
      setInvoices((invRes.data.items || invRes.data).filter(i => i.contact_id === id || i.customer_id === id));
      setJobs((jobsRes.data.items || jobsRes.data).filter(j => j.contact_id === id || j.customer_id === id));
      setPermits(permRes.data || []);
      setFiles(filesRes.data || []);
    } catch {
      console.error('Error fetching related data');
    }
  }, [id]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchContact();
      await fetchRelatedData();
      setLoading(false);
    };
    loadData();
  }, [fetchContact, fetchRelatedData]);

  // Fix F (2026-08-31) bug fix (testing_agent iteration 200, CRITICAL): the
  // Additional Contacts editor was only added to ContactsPage.jsx's dialog —
  // this page has its OWN separate Edit dialog that never got the section.
  const addContactPerson = () => {
    setForm(prev => ({
      ...prev,
      contact_people: [...(prev.contact_people || []), { id: crypto.randomUUID(), first_name: '', last_name: '', phone: '', phone_label: '', email: '', notes: '' }],
    }));
  };
  const updateContactPerson = (idx, field, value) => {
    setForm(prev => ({
      ...prev,
      contact_people: prev.contact_people.map((p, i) => (i === idx ? { ...p, [field]: value } : p)),
    }));
  };
  const removeContactPerson = (idx) => {
    setForm(prev => ({ ...prev, contact_people: prev.contact_people.filter((_, i) => i !== idx) }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) {
      toast.error(t('errors.requiredField'));
      return;
    }
    try {
      await api.put(`/contacts/${id}`, form);
      toast.success(t('success.updated'));
      setEditDialogOpen(false);
      fetchContact();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleFileUpload = async (e) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    setUploading(true);
    try {
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entity_type', 'contact');
        formData.append('entity_id', id);
        formData.append('description', '');

        await api.post('/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success(t('files.uploaded'));
      fetchRelatedData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    } finally {
      setUploading(false);
      setUploadDialogOpen(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm(t('common.confirmDelete'))) return;
    try {
      await api.delete(`/files/${fileId}`);
      toast.success(t('files.deleted'));
      fetchRelatedData();
    } catch {
      toast.error(t('errors.deleteError'));
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image': return <Image className="w-5 h-5 text-emerald-500" />;
      case 'video': return <Video className="w-5 h-5 text-purple-500" />;
      default: return <File className="w-5 h-5 text-blue-500" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Group files by type
  const images = files.filter(f => f.file_type === 'image');
  const videos = files.filter(f => f.file_type === 'video');
  const documents = files.filter(f => f.file_type === 'document');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#003366]/20 border-t-[#003366] rounded-full animate-spin" />
      </div>
    );
  }

  if (!contact) return null;

  // Calculate totals
  const totalEstimatesAmount = estimates.reduce((sum, e) => sum + (e.total || 0), 0);
  const totalInvoicesAmount = invoices.reduce((sum, i) => sum + (i.total || 0), 0);
  const paidInvoices = invoices.filter(i => i.status === 'paid');
  const pendingInvoices = invoices.filter(i => i.status !== 'paid');

  return (
    <div data-testid="contact-detail-page" className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate('/contacts')} 
          className="mt-0.5 h-9 w-9" 
          data-testid="back-to-contacts"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#003366] to-[#3399FF] flex items-center justify-center text-white text-lg font-bold shadow-lg">
                {contact.first_name?.[0]}{contact.last_name?.[0]}
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
                  {contact.first_name} {contact.last_name}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-700 capitalize">
                    {ACCOUNT_TYPE_CONFIG[contact.account_type]?.label || TYPE_LABELS[contact.type] || contact.type}
                  </span>
                  {contact.company_name && (
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      <Building className="w-3.5 h-3.5" /> {contact.company_name}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 self-start">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    size="sm" 
                    data-testid="new-dropdown-btn"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" /> New <ChevronDown className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem 
                    data-testid="new-contact-option"
                    onClick={() => navigate('/contacts', { state: { openNew: true } })}
                    className="cursor-pointer"
                  >
                    <UserPlus className="w-4 h-4 mr-2 text-slate-600" /> New Contact
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    data-testid="new-estimate-option"
                    onClick={() => navigate('/estimates', { state: { createFor: id, contactName: `${contact.first_name} ${contact.last_name}` } })}
                    className="cursor-pointer"
                  >
                    <FileText className="w-4 h-4 mr-2 text-blue-600" /> New Estimate
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    data-testid="new-invoice-option"
                    onClick={() => navigate('/invoices', { state: { createFor: id, contactName: `${contact.first_name} ${contact.last_name}` } })}
                    className="cursor-pointer"
                  >
                    <Receipt className="w-4 h-4 mr-2 text-emerald-600" /> New Invoice
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    data-testid="new-job-option"
                    onClick={() => navigate('/jobs', { state: { createFor: id, contactName: `${contact.first_name} ${contact.last_name}` } })}
                    className="cursor-pointer"
                  >
                    <Wrench className="w-4 h-4 mr-2 text-amber-600" /> New Job
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button 
                size="sm" 
                onClick={() => setEditDialogOpen(true)} 
                className="bg-[#003366] hover:bg-[#004080] text-white"
                data-testid="edit-contact-btn"
              >
                <Pencil className="w-4 h-4 mr-2" /> {t('common.edit')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Card className="bg-white border-gray-100 shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900">{estimates.length}</p>
                <p className="text-[11px] text-gray-500">{t('estimates.title')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-100 shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <Receipt className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900">{invoices.length}</p>
                <p className="text-[11px] text-gray-500">{t('invoices.title')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-100 shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                <Wrench className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900">{jobs.length}</p>
                <p className="text-[11px] text-gray-500">{t('jobs.title')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Fix (2026-09-14 cont.): related_projects ahora viene calculado EN VIVO
            desde el backend (customer_id -> projects), no del campo guardado que
            nunca se actualizaba. Mismo formato que las otras 3 (no se tocan). */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={relatedProjects.length === 0}>
            <Card
              data-testid="contact-projects-card"
              className={`bg-white border-gray-100 shadow-sm ${relatedProjects.length > 0 ? 'cursor-pointer hover:border-purple-200' : ''}`}
            >
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                    <FolderKanban className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-gray-900">{relatedProjects.length}</p>
                    <p className="text-[11px] text-gray-500">{t('projects.title')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </DropdownMenuTrigger>
          {relatedProjects.length > 0 && (
            <DropdownMenuContent align="start" className="w-64">
              {relatedProjects.map((p) => (
                <DropdownMenuItem
                  key={p.id}
                  data-testid={`contact-project-link-${p.id}`}
                  onClick={() => navigate(`/projects/${p.id}`)}
                  className="cursor-pointer"
                >
                  <FolderKanban className="w-3.5 h-3.5 mr-2 text-purple-600" />
                  <span className="truncate">{p.project_number || p.title || p.id}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          )}
        </DropdownMenu>
      </div>


      {/* Quick Contact & Financial Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Quick Contact Card */}
        <Card className="bg-gradient-to-br from-[#003366] to-[#004d99] text-white shadow-lg">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold mb-4 opacity-80">{t('contacts.quickContact')}</h3>
            <div className="space-y-3">
              {contact.phone && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <a 
                      href={`tel:${contact.phone}`} 
                      onClick={(e) => dialClick(e, contact.phone, `${contact.first_name} ${contact.last_name}`.trim())}
                      data-testid="contact-quick-phone-link"
                      className="flex-1 flex items-center gap-3 p-3 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                        <Phone className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-xs opacity-70">{t('common.phone')}</p>
                        <p className="font-semibold">{contact.phone}</p>
                      </div>
                    </a>
                    <ClickToCall
                      number={contact.phone}
                      name={`${contact.first_name} ${contact.last_name}`.trim()}
                      className="bg-white/20 text-white hover:bg-white/30 w-9 h-9"
                    />
                  </div>
                  {(contact.additional_phones || []).map((raw, i) => {
                    const pe = phoneEntryOf(raw);
                    const tag = [pe.name, pe.label].filter(Boolean).join(' · ');
                    return (
                      <div key={i} className="flex items-center gap-2">
                        <a href={`tel:${pe.phone}`} onClick={(e) => dialClick(e, pe.phone, `${contact.first_name} ${contact.last_name}`.trim())} className="flex-1 flex items-center gap-3 p-2 pl-16 rounded-lg bg-white/5 hover:bg-white/15 transition-colors">
                          <p className="text-sm opacity-90">{pe.phone}</p>
                          {tag && <p className="text-xs opacity-60">{tag}</p>}
                        </a>
                        <ClickToCall
                          number={pe.phone}
                          name={`${contact.first_name} ${contact.last_name}`.trim()}
                          className="bg-white/20 text-white hover:bg-white/30 w-8 h-8"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
              {contact.email && (
                <div className="space-y-1">
                  <a 
                    href={`mailto:${contact.email}`} 
                    className="flex items-center gap-3 p-3 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs opacity-70">{t('common.email')}</p>
                      <p className="font-semibold text-sm truncate max-w-[180px]">{contact.email}</p>
                    </div>
                  </a>
                  {(contact.additional_emails || []).map((raw, i) => {
                    const ee = emailEntryOf(raw);
                    return (
                      <a key={i} href={`mailto:${ee.email}`} className="flex items-center gap-3 p-2 pl-16 rounded-lg bg-white/5 hover:bg-white/15 transition-colors">
                        <p className="text-sm opacity-90 truncate max-w-[180px]">{ee.email}</p>
                        {ee.name && <p className="text-xs opacity-60">{ee.name}</p>}
                      </a>
                    );
                  })}
                </div>
              )}
              {contact.address && (
                <div className="flex items-start gap-3 p-3 rounded-lg bg-white/10">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div data-testid="contact-full-address">
                    <p className="text-xs opacity-70">{t('common.address')}</p>
                    <p className="font-semibold text-sm">{contact.address}</p>
                    {(contact.city || contact.state || contact.zip) && (
                      <p className="font-semibold text-sm">
                        {[contact.city, [stateFullName(contact.state), contact.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
                      </p>
                    )}
                    {contact.county && (
                      <p className="text-sm opacity-80">
                        {/county$/i.test(contact.county.trim()) ? contact.county : `${contact.county} County`}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {!contact.phone && !contact.email && !contact.address && (
                <div className="text-center py-4 opacity-60">
                  <p className="text-sm">{t('contacts.noContactInfo')}</p>
                </div>
              )}
              <button
                data-testid="contact-view-communications"
                onClick={() => setActiveTab('communications')}
                className="w-full flex items-center justify-center gap-2 p-2.5 rounded-lg bg-white/15 hover:bg-white/25 transition-colors text-sm font-semibold"
              >
                <MessagesSquare className="w-4 h-4" /> View calls, texts & emails
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Financial Summary Card */}
        <Card className="bg-white border-gray-100 shadow-sm lg:col-span-2">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-emerald-500" />
              {t('contacts.financialSummary')}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                <p className="text-xs text-blue-600 font-medium">{t('contacts.totalEstimates')}</p>
                <p className="text-xl font-bold text-blue-700 mt-1">${totalEstimatesAmount.toLocaleString()}</p>
                <p className="text-xs text-blue-500 mt-0.5">{estimates.length} {t('estimates.title').toLowerCase()}</p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                <p className="text-xs text-emerald-600 font-medium">{t('contacts.totalInvoiced')}</p>
                <p className="text-xl font-bold text-emerald-700 mt-1">${totalInvoicesAmount.toLocaleString()}</p>
                <p className="text-xs text-emerald-500 mt-0.5">{invoices.length} {t('invoices.title').toLowerCase()}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-50 border border-green-100">
                <p className="text-xs text-green-600 font-medium">{t('contacts.paidInvoices')}</p>
                <p className="text-xl font-bold text-green-700 mt-1">${paidInvoices.reduce((s, i) => s + (i.total || 0), 0).toLocaleString()}</p>
                <p className="text-xs text-green-500 mt-0.5">{paidInvoices.length} {t('invoices.status.paid').toLowerCase()}</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
                <p className="text-xs text-amber-600 font-medium">{t('contacts.pendingBalance')}</p>
                <p className="text-xl font-bold text-amber-700 mt-1">${pendingInvoices.reduce((s, i) => s + (i.total || 0), 0).toLocaleString()}</p>
                <p className="text-xs text-amber-500 mt-0.5">{pendingInvoices.length} {t('common.pending').toLowerCase()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="tabs-scroll">
          <TabsList className="bg-white border border-gray-200 h-10 w-full sm:w-auto">
            <TabsTrigger value="info" className="text-xs px-3 data-[state=active]:bg-[#003366] data-[state=active]:text-white">
              <User className="w-3.5 h-3.5 mr-1.5" /> {t('common.info')}
            </TabsTrigger>
            <TabsTrigger value="estimates" className="text-xs px-3 data-[state=active]:bg-[#003366] data-[state=active]:text-white">
              <FileText className="w-3.5 h-3.5 mr-1.5" /> {t('estimates.title')} ({estimates.length})
            </TabsTrigger>
            <TabsTrigger value="invoices" className="text-xs px-3 data-[state=active]:bg-[#003366] data-[state=active]:text-white">
              <Receipt className="w-3.5 h-3.5 mr-1.5" /> {t('invoices.title')} ({invoices.length})
            </TabsTrigger>
            <TabsTrigger value="jobs" className="text-xs px-3 data-[state=active]:bg-[#003366] data-[state=active]:text-white">
              <Wrench className="w-3.5 h-3.5 mr-1.5" /> {t('jobs.title')} ({jobs.length})
            </TabsTrigger>
            <TabsTrigger value="files" className="text-xs px-3 data-[state=active]:bg-[#003366] data-[state=active]:text-white">
              <Image className="w-3.5 h-3.5 mr-1.5" /> {t('files.title')} ({files.length})
            </TabsTrigger>
            <TabsTrigger value="permits" className="text-xs px-3 data-[state=active]:bg-[#003366] data-[state=active]:text-white">
              <ClipboardList className="w-3.5 h-3.5 mr-1.5" /> {t('permits.title')} ({permits.length})
            </TabsTrigger>
            <TabsTrigger value="communications" data-testid="contact-communications-tab" className="text-xs px-3 data-[state=active]:bg-[#003366] data-[state=active]:text-white">
              <MessagesSquare className="w-3.5 h-3.5 mr-1.5" /> Communications
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Info Tab */}
        <TabsContent value="info" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Contact Information */}
            <Card className="lg:col-span-2 bg-white border-gray-100 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold font-['Barlow'] flex items-center gap-2">
                  <User className="w-4 h-4 text-[#3399FF]" /> {t('contacts.contactInfo')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Contact Details Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{t('contacts.firstName')}</p>
                    <p className="text-sm font-medium text-gray-900 mt-0.5">{contact.first_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{t('contacts.lastName')}</p>
                    <p className="text-sm font-medium text-gray-900 mt-0.5">{contact.last_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{t('common.type')}</p>
                    <p className="text-sm font-medium text-gray-900 mt-0.5 capitalize">{ACCOUNT_TYPE_CONFIG[contact.account_type]?.label || TYPE_LABELS[contact.type] || contact.type}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{t('contacts.company')}</p>
                    <p className="text-sm font-medium text-gray-900 mt-0.5">{contact.company_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{t('common.phone')}</p>
                    {contact.phone ? (
                      <div className="space-y-0.5 mt-0.5">
                        <div className="flex items-center gap-1.5">
                          <a href={`tel:${contact.phone}`} onClick={(e) => dialClick(e, contact.phone, `${contact.first_name} ${contact.last_name}`.trim())} className="text-sm font-medium text-blue-600 hover:underline flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5" /> {contact.phone}
                          </a>
                          <ClickToCall number={contact.phone} name={`${contact.first_name} ${contact.last_name}`.trim()} />
                        </div>
                        {(contact.additional_phones || []).map((raw, i) => {
                          const pe = phoneEntryOf(raw);
                          const tag = [pe.name, pe.label].filter(Boolean).join(' · ');
                          return (
                            <div key={i} className="flex items-center gap-1.5 ml-5">
                              <a href={`tel:${pe.phone}`} onClick={(e) => dialClick(e, pe.phone, `${contact.first_name} ${contact.last_name}`.trim())} className="text-xs text-blue-500 hover:underline flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {pe.phone}
                              </a>
                              {tag && <span className="text-[11px] text-gray-400">{tag}</span>}
                              <ClickToCall number={pe.phone} name={`${contact.first_name} ${contact.last_name}`.trim()} />
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 mt-0.5">-</p>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{t('common.email')}</p>
                    {contact.email ? (
                      <div className="space-y-0.5 mt-0.5">
                        <a href={`mailto:${contact.email}`} className="text-sm font-medium text-blue-600 hover:underline flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" /> {contact.email}
                        </a>
                        {(contact.additional_emails || []).map((raw, i) => {
                          const ee = emailEntryOf(raw);
                          return (
                            <a key={i} href={`mailto:${ee.email}`} className="text-xs text-blue-500 hover:underline flex items-center gap-1 ml-5">
                              <Mail className="w-3 h-3" /> {ee.email}
                              {ee.name && <span className="text-[11px] text-gray-400 ml-1">{ee.name}</span>}
                            </a>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 mt-0.5">-</p>
                    )}
                  </div>
                </div>

                {/* Fix F (2026-08-31): Additional Contacts — property managers where
                    one company has 3-4 people (who pays, the accountant, the PM).
                    Commercial contacts only; each person's phone is clickable
                    reusing the same ClickToCall already used above. */}
                {contact.account_type === 'commercial' && (contact.contact_people || []).length > 0 && (
                  <div className="pt-4 border-t border-gray-100" data-testid="additional-contacts-view">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-3">Additional Contacts</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {contact.contact_people.map((p, i) => (
                        <div key={p.id || i} className="rounded-lg border border-gray-100 p-3" data-testid={`additional-contact-view-${i}`}>
                          <p className="text-sm font-medium text-gray-900">{[p.first_name, p.last_name].filter(Boolean).join(' ') || 'Contact'}</p>
                          {p.phone && (
                            <div className="flex items-center gap-1.5 mt-1">
                              <a href={`tel:${p.phone}`} onClick={(e) => dialClick(e, p.phone, [p.first_name, p.last_name].filter(Boolean).join(' '))} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {p.phone}{p.phone_label ? ` (${p.phone_label})` : ''}
                              </a>
                              <ClickToCall number={p.phone} name={[p.first_name, p.last_name].filter(Boolean).join(' ')} />
                            </div>
                          )}
                          {p.email && (
                            <a href={`mailto:${p.email}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1">
                              <Mail className="w-3 h-3" /> {p.email}
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Address Section */}
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> Addresses
                      {(contact.addresses || []).length > 0 && (
                        <span className="text-gray-500 ml-1">({(contact.addresses || []).length})</span>
                      )}
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => setManageAddrOpen(true)}
                      data-testid="manage-addresses-btn"
                    >
                      <MapPin className="w-3 h-3 mr-1" /> Manage Addresses
                    </Button>
                  </div>

                  {/* List multiple addresses if available */}
                  {(contact.addresses || []).length > 0 ? (
                    <div className="space-y-2">
                      {(contact.addresses || []).map(addr => (
                        <div key={addr.id} className="flex items-start gap-2 p-2.5 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors" data-testid={`detail-address-${addr.id}`}>
                          <MapPin className="w-3.5 h-3.5 text-[#3399FF] mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className="text-[10px] font-semibold text-gray-700 uppercase tracking-wide">{addr.label}</span>
                              {addr.is_primary && <span className="text-[9px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-semibold">PRIMARY</span>}
                            </div>
                            <p className="text-sm text-gray-900">{addr.address}</p>
                            <p className="text-xs text-gray-500">
                              {[addr.city, addr.state, addr.zip].filter(Boolean).join(', ')}
                              {addr.county ? ` · ${addr.county} County` : ''}
                            </p>
                            {addr.parcel_id && <p className="text-[10px] text-gray-400 font-mono mt-0.5">Parcel: {addr.parcel_id}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Backwards-compat: show legacy single mailing address fields if no `addresses` array yet */
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <div className="col-span-2 sm:col-span-3">
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{t('common.address')}</p>
                        <p className="text-sm font-medium text-gray-900 mt-0.5">{contact.address || '-'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{t('common.city')}</p>
                        <p className="text-sm font-medium text-gray-900 mt-0.5">{contact.city || '-'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{t('common.county')}</p>
                        <p className="text-sm font-medium text-gray-900 mt-0.5">{contact.county || '-'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{t('common.state')}</p>
                        <p className="text-sm font-medium text-gray-900 mt-0.5">{contact.state || '-'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{t('common.zip')}</p>
                        <p className="text-sm font-medium text-gray-900 mt-0.5">{contact.zip || '-'}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Subcontractor Info Section */}
                {(contact.type === 'subcontractor' || contact.account_type === 'subcontractor') && 
                 (contact.subcontractor_category || contact.payment_type || contact.commission_type) && (
                  <div className="pt-4 border-t border-gray-100">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-3 flex items-center gap-1">
                      <Wrench className="w-3 h-3" /> Subcontractor Details
                    </p>
                    <div className="grid grid-cols-3 gap-4">
                      {contact.subcontractor_category && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Category</p>
                          <p className="text-sm font-medium text-gray-900 mt-0.5">{contact.subcontractor_category}</p>
                        </div>
                      )}
                      {contact.payment_type && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Payment Type</p>
                          <p className="text-sm font-medium text-gray-900 mt-0.5">{contact.payment_type}</p>
                        </div>
                      )}
                      {contact.commission_type && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Commission</p>
                          <p className="text-sm font-medium text-gray-900 mt-0.5">{contact.commission_type}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes Section */}
                {contact.notes && (
                  <div className="pt-4 border-t border-gray-100">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-2">{t('common.notes')}</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{contact.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions & Summary */}
            <div className="space-y-4">
              {/* Quick Actions */}
              <Card className="bg-white border-gray-100 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold font-['Barlow']">{t('common.actions')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button 
                    onClick={() => navigate('/estimates', { state: { createFor: id, contactName: `${contact.first_name} ${contact.last_name}` } })}
                    className="w-full justify-start bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200"
                    variant="ghost"
                  >
                    <FileText className="w-4 h-4 mr-2" /> {t('contacts.createEstimate')}
                  </Button>
                  <Button 
                    onClick={() => navigate('/invoices', { state: { createFor: id, contactName: `${contact.first_name} ${contact.last_name}` } })}
                    className="w-full justify-start bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200"
                    variant="ghost"
                  >
                    <Receipt className="w-4 h-4 mr-2" /> {t('contacts.createInvoice')}
                  </Button>
                  <Button 
                    onClick={() => navigate('/jobs', { state: { createFor: id, contactName: `${contact.first_name} ${contact.last_name}` } })}
                    className="w-full justify-start bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200"
                    variant="ghost"
                  >
                    <Wrench className="w-4 h-4 mr-2" /> {t('jobs.newJob')}
                  </Button>
                </CardContent>
              </Card>

              {/* Financial Summary */}
              <Card className="bg-white border-gray-100 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold font-['Barlow'] flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-[#3399FF]" /> {t('reports.financialSummary')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">{t('estimates.title')}</span>
                    <span className="text-sm font-semibold text-gray-900">{formatCurrency(totalEstimatesAmount)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">{t('invoices.title')}</span>
                    <span className="text-sm font-semibold text-gray-900">{formatCurrency(totalInvoicesAmount)}</span>
                  </div>
                  <div className="pt-2 border-t border-gray-100">
                    <div className="flex justify-between items-center text-emerald-600">
                      <span className="text-xs">{t('invoices.statuses.paid')} ({paidInvoices.length})</span>
                      <span className="text-sm font-semibold">{formatCurrency(paidInvoices.reduce((s, i) => s + (i.total || 0), 0))}</span>
                    </div>
                    <div className="flex justify-between items-center text-amber-600 mt-1">
                      <span className="text-xs">{t('invoices.statuses.pending')} ({pendingInvoices.length})</span>
                      <span className="text-sm font-semibold">{formatCurrency(pendingInvoices.reduce((s, i) => s + (i.total || 0), 0))}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Estimates Tab */}
        <TabsContent value="estimates" className="mt-4">
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold font-['Barlow'] flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-500" /> {t('estimates.title')}
              </CardTitle>
              <Button 
                size="sm"
                onClick={() => navigate('/estimates', { state: { createFor: id, contactName: `${contact.first_name} ${contact.last_name}` } })}
                className="bg-[#003366] hover:bg-[#004080] text-white"
              >
                <Plus className="w-4 h-4 mr-1" /> {t('estimates.newEstimate')}
              </Button>
            </CardHeader>
            <CardContent>
              {estimates.length === 0 ? (
                <div className="py-8 text-center text-gray-400">
                  <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>{t('estimates.noEstimates')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {estimates.map(est => {
                    const estAddress = est.well_address || 
                      (est.mailing_address?.address ? `${est.mailing_address.address}${est.mailing_address.city ? `, ${est.mailing_address.city}` : ''}` : '') ||
                      (contact.address ? `${contact.address}${contact.city ? `, ${contact.city}` : ''}` : '');
                    return (
                      <div 
                        key={est.id} 
                        className="p-4 rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50/30 cursor-pointer transition-all"
                        onClick={() => navigate(`/estimates`, { state: { openEstimateId: est.id } })}
                        data-testid={`estimate-link-${est.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                              <FileText className="w-4.5 h-4.5 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-900">#{est.estimate_number || est.id?.slice(-6)}</p>
                              <p className="text-xs text-gray-400">{formatDate(est.created_at)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <StatusBadge status={est.status} />
                            <span className="text-sm font-bold text-gray-900">{formatCurrency(est.total)}</span>
                            <ExternalLink className="w-3.5 h-3.5 text-gray-300" />
                          </div>
                        </div>
                        {estAddress && (
                          <div className="flex items-center gap-1.5 mt-2 ml-12">
                            <MapPin className="w-3 h-3 text-gray-400 shrink-0" />
                            <p className="text-xs text-gray-500 truncate" data-testid={`estimate-address-${est.id}`}>{estAddress}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="mt-4">
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold font-['Barlow'] flex items-center gap-2">
                <Receipt className="w-4 h-4 text-emerald-500" /> {t('invoices.title')}
              </CardTitle>
              <Button 
                size="sm"
                onClick={() => navigate('/invoices', { state: { createFor: id, contactName: `${contact.first_name} ${contact.last_name}` } })}
                className="bg-[#003366] hover:bg-[#004080] text-white"
              >
                <Plus className="w-4 h-4 mr-1" /> {t('invoices.newInvoice')}
              </Button>
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <div className="py-8 text-center text-gray-400">
                  <Receipt className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>{t('invoices.noInvoices')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {invoices.map(inv => {
                    const invAddress = inv.well_address || 
                      (inv.mailing_address?.address ? `${inv.mailing_address.address}${inv.mailing_address.city ? `, ${inv.mailing_address.city}` : ''}` : '') ||
                      (contact.address ? `${contact.address}${contact.city ? `, ${contact.city}` : ''}` : '');
                    const paymentsCount = inv.payments?.length || 0;
                    const amountPaid = inv.amount_paid || 0;
                    const balanceDue = inv.balance_due ?? (inv.total - amountPaid);
                    return (
                      <div 
                        key={inv.id} 
                        className="p-4 rounded-lg border border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/30 cursor-pointer transition-all"
                        onClick={() => navigate(`/invoices`, { state: { openInvoiceId: inv.id } })}
                        data-testid={`invoice-link-${inv.id}`}
                      >
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                              <Receipt className="w-4.5 h-4.5 text-emerald-600" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-900">#{inv.invoice_number || inv.id?.slice(-6)}</p>
                              <p className="text-xs text-gray-400">{formatDate(inv.created_at)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <StatusBadge status={inv.status} />
                            <ExternalLink className="w-3.5 h-3.5 text-gray-300" />
                          </div>
                        </div>
                        {invAddress && (
                          <div className="flex items-center gap-1.5 mb-2 ml-12">
                            <MapPin className="w-3 h-3 text-gray-400 shrink-0" />
                            <p className="text-xs text-gray-500 truncate" data-testid={`invoice-address-${inv.id}`}>{invAddress}</p>
                          </div>
                        )}
                        <div className="grid grid-cols-3 gap-2 ml-12" data-testid={`invoice-financials-${inv.id}`}>
                          <div className="bg-slate-50 rounded-md px-2.5 py-1.5">
                            <p className="text-[10px] text-gray-400 uppercase tracking-wide">Total</p>
                            <p className="text-sm font-bold text-gray-900">{formatCurrency(inv.total)}</p>
                          </div>
                          <div className="bg-emerald-50 rounded-md px-2.5 py-1.5">
                            <p className="text-[10px] text-emerald-500 uppercase tracking-wide">Pagos ({paymentsCount})</p>
                            <p className="text-sm font-bold text-emerald-700">{formatCurrency(amountPaid)}</p>
                          </div>
                          <div className={`rounded-md px-2.5 py-1.5 ${balanceDue > 0 ? 'bg-amber-50' : 'bg-green-50'}`}>
                            <p className={`text-[10px] uppercase tracking-wide ${balanceDue > 0 ? 'text-amber-500' : 'text-green-500'}`}>Balance</p>
                            <p className={`text-sm font-bold ${balanceDue > 0 ? 'text-amber-700' : 'text-green-700'}`}>{formatCurrency(balanceDue)}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Jobs Tab */}
        <TabsContent value="jobs" className="mt-4">
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold font-['Barlow'] flex items-center gap-2">
                <Wrench className="w-4 h-4 text-amber-500" /> {t('jobs.title')}
              </CardTitle>
              <Button 
                size="sm"
                onClick={() => navigate('/jobs', { state: { createFor: id, contactName: `${contact.first_name} ${contact.last_name}` } })}
                className="bg-[#003366] hover:bg-[#004080] text-white"
              >
                <Plus className="w-4 h-4 mr-1" /> {t('jobs.newJob')}
              </Button>
            </CardHeader>
            <CardContent>
              {jobs.length === 0 ? (
                <div className="py-8 text-center text-gray-400">
                  <Wrench className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>{t('jobs.noJobs')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {jobs.map(job => (
                    <div 
                      key={job.id} 
                      className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/jobs/${job.id}`)}
                      data-testid={`job-link-${job.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                          <Wrench className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{job.title || `Job #${job.id?.slice(-6)}`}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            {job.scheduled_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" /> {formatDate(job.scheduled_date)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <StatusBadge status={job.status} />
                        <ExternalLink className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Files Tab */}
        <TabsContent value="files" className="mt-4">
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold font-['Barlow'] flex items-center gap-2">
                <Image className="w-4 h-4 text-emerald-500" /> {t('files.photosAndFiles')}
              </CardTitle>
              <label className="cursor-pointer">
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*,.pdf,.doc,.docx"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={uploading}
                />
                <Button 
                  size="sm"
                  className="bg-[#003366] hover:bg-[#004080] text-white"
                  disabled={uploading}
                  asChild
                >
                  <span>
                    <Upload className="w-4 h-4 mr-1" /> 
                    {uploading ? t('files.uploading') : t('files.upload')}
                  </span>
                </Button>
              </label>
            </CardHeader>
            <CardContent>
              {files.length === 0 ? (
                <div className="py-12 text-center text-gray-400">
                  <Image className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">{t('files.noFiles')}</p>
                  <p className="text-sm mt-1">{t('files.uploadToAdd')}</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Images Section */}
                  {images.length > 0 && (
                    <div>
                      <h4 className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3 flex items-center gap-2">
                        <Image className="w-4 h-4" /> {t('files.images')} ({images.length})
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                        {images.map(file => (
                          <div 
                            key={file.id} 
                            className="group relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-50 cursor-pointer hover:shadow-md transition-shadow"
                            onClick={() => setPreviewImage(file)}
                          >
                            <img 
                              src={file.url} 
                              alt={file.name} 
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors">
                              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7 bg-white/90 hover:bg-white text-red-500"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteFile(file.id); }}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                              <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                <p className="text-white text-xs truncate">{file.name}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Videos Section */}
                  {videos.length > 0 && (
                    <div>
                      <h4 className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3 flex items-center gap-2">
                        <Video className="w-4 h-4" /> {t('files.videos')} ({videos.length})
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {videos.map(file => (
                          <div 
                            key={file.id} 
                            className="group relative aspect-video rounded-lg overflow-hidden border border-gray-200 bg-gray-900"
                          >
                            <video 
                              src={file.url} 
                              className="w-full h-full object-cover"
                              controls
                            />
                            <div className="absolute top-2 right-2">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 bg-white/90 hover:bg-white text-red-500"
                                onClick={() => handleDeleteFile(file.id)}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Documents Section */}
                  {documents.length > 0 && (
                    <div>
                      <h4 className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3 flex items-center gap-2">
                        <File className="w-4 h-4" /> {t('files.documents')} ({documents.length})
                      </h4>
                      <div className="space-y-2">
                        {documents.map(file => (
                          <div 
                            key={file.id} 
                            className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              {getFileIcon(file.file_type)}
                              <div>
                                <a 
                                  href={file.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-sm font-medium text-gray-900 hover:text-blue-600"
                                >
                                  {file.name}
                                </a>
                                <p className="text-xs text-gray-500">{formatFileSize(file.size)} - {formatDate(file.created_at)}</p>
                              </div>
                            </div>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteFile(file.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Permits Tab */}
        <TabsContent value="permits" className="mt-4">
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold font-['Barlow'] flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-purple-500" /> {t('permits.title')} / {t('permits.forms')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {permits.length === 0 ? (
                <div className="py-8 text-center text-gray-400">
                  <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>{t('permits.noPermits')}</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Permit Applications */}
                  {permits.filter(p => p.type !== 'well_completion').length > 0 && (
                    <div>
                      <h4 className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3 flex items-center gap-2">
                        <ClipboardList className="w-4 h-4" /> Permit Applications ({permits.filter(p => p.type !== 'well_completion').length})
                      </h4>
                      <div className="space-y-2">
                        {permits.filter(p => p.type !== 'well_completion').map(permit => (
                          <div 
                            key={permit.id} 
                            className="flex items-center justify-between p-4 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => navigate(`/permits`, { state: { openPermitId: permit.id } })}
                            data-testid={`permit-link-${permit.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                                <ClipboardList className="w-6 h-6 text-blue-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  Permit Application #{permit.permit_number || permit.id?.slice(-6)}
                                </p>
                                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" /> {permit.county || 'Sin condado'}
                                  </span>
                                  <span>{formatDate(permit.created_at)}</span>
                                </div>
                                {permit.well_location?.address && (
                                  <p className="text-xs text-gray-400 mt-1 truncate max-w-[300px]">
                                    {permit.well_location.address}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <StatusBadge status={permit.status} />
                              <ExternalLink className="w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Completion Reports */}
                  {permits.filter(p => p.type === 'well_completion').length > 0 && (
                    <div>
                      <h4 className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Completion Reports ({permits.filter(p => p.type === 'well_completion').length})
                      </h4>
                      <div className="space-y-2">
                        {permits.filter(p => p.type === 'well_completion').map(permit => (
                          <div 
                            key={permit.id} 
                            className="flex items-center justify-between p-4 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => navigate(`/permits`, { state: { openPermitId: permit.id } })}
                            data-testid={`permit-link-${permit.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-lg bg-emerald-50 flex items-center justify-center">
                                <FileText className="w-6 h-6 text-emerald-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  Completion Report #{permit.permit_number || permit.id?.slice(-6)}
                                </p>
                                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" /> {permit.county || 'Sin condado'}
                                  </span>
                                  <span>{formatDate(permit.created_at)}</span>
                                </div>
                                {permit.notes && (
                                  <p className="text-xs text-gray-400 mt-1 truncate max-w-[300px]">
                                    {permit.notes}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <StatusBadge status={permit.status} />
                              <ExternalLink className="w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Communications Tab — calls (with recordings), texts and emails */}
        <TabsContent value="communications" className="mt-4">
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold font-['Barlow'] flex items-center gap-2">
                <MessagesSquare className="w-4 h-4 text-[#3399FF]" /> Communications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CommunicationsTimeline
                contactId={id}
                contactName={`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.company_name}
                phone={contact.phone}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Activity Log (read-only) */}
      <div className="mt-6">
        <ActivityLog entityType="contacts" entityId={id} />
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl">{t('contacts.editContact')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-5">
            {/* Account Type Selection */}
            <div className="space-y-3 p-4 bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl border border-blue-100">
              <Label className="text-sm font-semibold text-gray-700">Account Type *</Label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {ACCOUNT_TYPES.map(at => {
                  const cfg = ACCOUNT_TYPE_CONFIG[at];
                  const isSelected = form.account_type === at;
                  return (
                    <button
                      key={at}
                      type="button"
                      data-testid={`edit-account-type-${at}`}
                      onClick={() => setForm({...form, account_type: at, type: cfg.type, is_customer: cfg.is_customer})}
                      className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1.5 ${
                        isSelected
                          ? 'border-[#003366] bg-[#003366] text-white shadow-lg' 
                          : 'border-gray-200 bg-white hover:border-[#3399FF] hover:bg-blue-50'
                      }`}
                    >
                      {(at === 'residential' || at === 'other') && <User className="w-5 h-5" />}
                      {at === 'commercial' && <Building className="w-5 h-5" />}
                      {at === 'subcontractor' && <Wrench className="w-5 h-5" />}
                      {at === 'vendor' && <Building className="w-5 h-5" />}
                      <span className="text-xs font-medium">{cfg.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('contacts.firstName')} *</Label>
                <Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} placeholder={t('contacts.firstName')} />
              </div>
              <div className="space-y-2">
                <Label>{t('contacts.lastName')}</Label>
                <Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} placeholder={t('contacts.lastName')} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>{t('contacts.company')}</Label>
                <Input value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} placeholder={t('contacts.company')} />
              </div>
            </div>

            {/* Subcontractor-specific fields */}
            {form.account_type === 'subcontractor' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-amber-800">Category</Label>
                  <Select value={form.subcontractor_category} onValueChange={v => setForm({...form, subcontractor_category: v})}>
                    <SelectTrigger data-testid="edit-subcontractor-category" className="bg-white"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      {['Driller', 'Pump Installer', 'General Labor'].map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-amber-800">Payment Type</Label>
                  <Select value={form.payment_type} onValueChange={v => setForm({...form, payment_type: v})}>
                    <SelectTrigger data-testid="edit-subcontractor-payment" className="bg-white"><SelectValue placeholder="Select payment" /></SelectTrigger>
                    <SelectContent>
                      {['Labor', 'Trailer', 'On-site', 'Hourly', 'Per Job'].map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-amber-800">Commission</Label>
                  <Select value={form.commission_type} onValueChange={v => setForm({...form, commission_type: v})}>
                    <SelectTrigger data-testid="edit-subcontractor-commission" className="bg-white"><SelectValue placeholder="Select commission" /></SelectTrigger>
                    <SelectContent>
                      {['100%', 'Fixed Cost', 'Percentage', 'Negotiable'].map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.phone')}</Label>
                <div className="flex gap-2">
                  <Input 
                    data-testid="edit-contact-phone"
                    value={form.phone} 
                    onChange={e => setForm({...form, phone: formatPhoneNumber(e.target.value)})} 
                    placeholder="(786) 510-4512" 
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" size="icon" className="h-9 w-9 flex-shrink-0" onClick={() => setForm({...form, additional_phones: [...(form.additional_phones || []), '']})}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {(form.additional_phones || []).map((raw, idx) => {
                  const pe = phoneEntryOf(raw);
                  const update = (field, value) => {
                    const phones = [...form.additional_phones];
                    const next = { ...pe, [field]: value };
                    phones[idx] = (next.name || next.label) ? next : next.phone;
                    setForm({ ...form, additional_phones: phones });
                  };
                  return (
                    <div key={idx} className="flex gap-2">
                      <Input
                        data-testid={`edit-contact-phone-name-${idx + 1}`}
                        value={pe.name}
                        onChange={e => update('name', e.target.value)}
                        placeholder="Name (optional)"
                        className="w-28 flex-shrink-0"
                      />
                      <Input
                        data-testid={`edit-contact-phone-${idx + 1}`}
                        value={pe.phone}
                        onChange={e => update('phone', formatPhoneNumber(e.target.value))}
                        placeholder={`${t('common.phone')} ${idx + 2}`}
                        className="flex-1"
                      />
                      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 flex-shrink-0 text-red-400 hover:text-red-600" onClick={() => setForm({...form, additional_phones: form.additional_phones.filter((_, i) => i !== idx)})}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  );
                })}
              </div>
              <div className="space-y-2">
                <Label>{t('common.email')}</Label>
                <div className="flex gap-2">
                  <Input 
                    data-testid="edit-contact-email"
                    value={form.email} 
                    onChange={e => setForm({...form, email: e.target.value})} 
                    placeholder="correo@ejemplo.com" 
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" size="icon" className="h-9 w-9 flex-shrink-0" onClick={() => setForm({...form, additional_emails: [...(form.additional_emails || []), '']})}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {(form.additional_emails || []).map((raw, idx) => {
                  const ee = emailEntryOf(raw);
                  const update = (field, value) => {
                    const emails = [...form.additional_emails];
                    const next = { ...ee, [field]: value };
                    emails[idx] = next.name ? next : next.email;
                    setForm({ ...form, additional_emails: emails });
                  };
                  return (
                    <div key={idx} className="flex gap-2">
                      <Input
                        data-testid={`edit-contact-email-name-${idx + 1}`}
                        value={ee.name}
                        onChange={e => update('name', e.target.value)}
                        placeholder="Name (optional)"
                        className="w-28 flex-shrink-0"
                      />
                      <Input
                        data-testid={`edit-contact-email-${idx + 1}`}
                        value={ee.email}
                        onChange={e => update('email', e.target.value)}
                        placeholder={`${t('common.email')} ${idx + 2}`}
                        className="flex-1"
                      />
                      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 flex-shrink-0 text-red-400 hover:text-red-600" onClick={() => setForm({...form, additional_emails: form.additional_emails.filter((_, i) => i !== idx)})}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Fix F (2026-08-31): Additional Contacts editor — same block as
                ContactsPage.jsx's dialog, now also on this page's own Edit dialog. */}
            {form.account_type === 'commercial' && (
              <div className="space-y-3" data-testid="additional-contacts-section">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold text-gray-700">Additional Contacts</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addContactPerson} data-testid="add-contact-person-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Person
                  </Button>
                </div>
                {(form.contact_people || []).map((p, idx) => (
                  <div key={p.id || idx} className="rounded-lg border border-gray-200 p-3 space-y-2" data-testid={`contact-person-card-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400 font-medium">Person {idx + 1}</span>
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => removeContactPerson(idx)} data-testid={`remove-contact-person-${idx}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="First name" value={p.first_name} onChange={e => updateContactPerson(idx, 'first_name', e.target.value)} data-testid={`contact-person-first-name-${idx}`} />
                      <Input placeholder="Last name" value={p.last_name} onChange={e => updateContactPerson(idx, 'last_name', e.target.value)} data-testid={`contact-person-last-name-${idx}`} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Phone" value={p.phone} onChange={e => updateContactPerson(idx, 'phone', e.target.value)} data-testid={`contact-person-phone-${idx}`} />
                      <Input placeholder="Label (office/cell/...)" value={p.phone_label} onChange={e => updateContactPerson(idx, 'phone_label', e.target.value)} data-testid={`contact-person-phone-label-${idx}`} />
                    </div>
                    <Input placeholder="Email" value={p.email} onChange={e => updateContactPerson(idx, 'email', e.target.value)} data-testid={`contact-person-email-${idx}`} />
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-3">
              <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-400" /> Mailing Address
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-3">
                  <AddressAutocomplete
                    value={form.address}
                    onChange={(addressData) => {
                      const isFullSelection = addressData.lat !== null && addressData.lat !== undefined;
                      setForm(prev => ({
                        ...prev,
                        address: addressData.address || '',
                        city: isFullSelection ? (addressData.city || '') : '',
                        county: isFullSelection ? (addressData.county || '') : '',
                        state: isFullSelection ? (stateNameToCode(addressData.state) || prev.state || 'FL') : prev.state,
                        zip: isFullSelection ? (addressData.zip_code || '') : '',
                      }));
                    }}
                    placeholder="Mailing Address"
                  />
                </div>
                <Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} placeholder={t('common.city')} />
                <Select value={form.state || 'FL'} onValueChange={v => setForm({...form, state: v, county: v === 'FL' ? form.county : ''})}>
                  <SelectTrigger data-testid="contact-detail-state-select"><SelectValue placeholder="State" /></SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(s => <SelectItem key={s.code} value={s.code}>{s.code} - {s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input value={form.zip} onChange={e => setForm({...form, zip: e.target.value})} placeholder={t('common.zip')} />
                <div className="sm:col-span-3">
                  {form.state === 'FL' ? (
                    <Select value={form.county} onValueChange={v => setForm({...form, county: v})}>
                      <SelectTrigger data-testid="contact-detail-county-select"><SelectValue placeholder={t('common.county')} /></SelectTrigger>
                      <SelectContent>
                        {counties.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={form.county}
                      onChange={e => setForm({...form, county: e.target.value})}
                      placeholder={t('common.county')}
                      data-testid="contact-detail-county-input"
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('common.notes')}</Label>
              <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder={t('common.notes') + '...'} rows={2} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button type="submit" className="bg-[#003366] hover:bg-[#004080] text-white">
                {t('contacts.saveChanges')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-4xl p-0 bg-black/95 border-0">
          <div className="relative">
            <Button
              size="icon"
              variant="ghost"
              className="absolute top-2 right-2 z-10 h-10 w-10 bg-white/10 hover:bg-white/20 text-white"
              onClick={() => setPreviewImage(null)}
            >
              <X className="w-5 h-5" />
            </Button>
            {previewImage && (
              <img 
                src={previewImage.url} 
                alt={previewImage.name} 
                className="w-full max-h-[80vh] object-contain"
              />
            )}
            {previewImage && (
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                <p className="text-white font-medium">{previewImage.name}</p>
                <p className="text-white/70 text-sm">{formatFileSize(previewImage.size)} - {formatDate(previewImage.created_at)}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Addresses Dialog */}
      <ManageAddresses
        open={manageAddrOpen}
        onClose={() => setManageAddrOpen(false)}
        contactId={id}
        contactName={contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : ''}
        onChanged={fetchContact}
      />
    </div>
  );
}
